import { useEffect, useMemo, useRef, useState } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import {
  ArrowLeft,
  ArrowRight,
  Check,
  Plus,
  Trash2,
  Loader2,
  CheckCircle2,
  Clock,
  ChevronDown,
  Share2,
  Copy,
} from "lucide-react";

import {
  Combobox,
} from "../components/forms/Combobox";
import { SegmentedControl } from "../components/forms/SegmentedControl";
import {
  DateOfBirthPicker,
  calcAge,
} from "../components/forms/DateOfBirthPicker";
import { MonthYearPicker } from "../components/forms/MonthYearPicker";
import { validatePostalCode } from "../components/forms/postalValidation";
import { SignUpShell } from "../components/forms/SignUpShell";
import { WhyWeAsk } from "../components/forms/WhyWeAsk";
import { ValidationSummary } from "../components/forms/ValidationSummary";
import FollowStrip from "../components/FollowStrip";
import { usePageMeta } from "../hooks/usePageMeta";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const STORAGE_KEY = "meditalk:patient-register:v2";

const STEPS = [
  { id: 1, label: "About you" },
  { id: 2, label: "Location & contact" },
  { id: 3, label: "Your conditions" },
  { id: 4, label: "Consents" },
];

const DEFAULT_CONDITION = {
  condition_label: "",
  specify: "",
  diagnosis_date: "",
  severity: "",
  treatment: "",
  other_info: "",
};

const DEFAULT_FORM = {
  first_name: "",
  last_name: "",
  email: "",
  phone_prefix: "",
  phone_number: "",
  date_of_birth: "",
  gender: "",
  ethnicity: "",
  ethnicity_specify: "",
  country: "",
  postal_code: "",
  conditions: [{ ...DEFAULT_CONDITION }],
  consents: {
    truthful: false,
    payment_sharing: false,
    invitation: false,
  },
};

const CONSENT_ITEMS = [
  {
    key: "truthful",
    icon: "✓",
    title: "Information is truthful",
    summary:
      "Everything I've shared above is true to the best of my knowledge.",
    full:
      "I agree that I voluntarily provided the above information and that it is true to the best of my knowledge and belief.",
  },
  {
    key: "payment_sharing",
    icon: "💳",
    title: "Payment processing",
    summary:
      "MediTalk can share my details with payment partners (e.g. e-voucher providers, MediTalk's bank Lloyds) so I can be paid for my time.",
    full:
      "To receive payment for my time and contribution, I consent to having my personal information (e.g. name, email and/or bank account information) that I provide shared with third parties (e.g. companies that provide e-voucher services or MediTalk's bank, Lloyds).",
  },
  {
    key: "invitation",
    icon: "📧",
    title: "Study invitations",
    summary:
      "MediTalk can keep my contact details on file to invite me to relevant studies.",
    full:
      "I consent to having my contact information retained by MediTalk for the purpose of being invited to take part in research studies.",
  },
];

// ============================================================
// Page
// ============================================================
export default function PatientSignUpPage() {
  usePageMeta({
    path: "/patient/sign-up",
    title: "Join MediTalk as a patient , Free 3-minute sign-up",
    description:
      "Create your patient profile in 3 minutes. Share which conditions you live with and we'll only invite you to studies that genuinely match.",
  });

  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [duplicateEmail, setDuplicateEmail] = useState(null);
  const [optionsLoading, setOptionsLoading] = useState(true);
  const [options, setOptions] = useState({
    countries: [],
    conditions: [],
    popular_conditions: [],
    ethnicities: [],
    genders: [],
    severities: [],
  });
  const [form, setForm] = useState(DEFAULT_FORM);
  const [errors, setErrors] = useState({});
  const [submitAttempted, setSubmitAttempted] = useState(false);

  // Load saved progress (auto-resume)
  const restoredRef = useRef(false);
  useEffect(() => {
    if (restoredRef.current) return;
    restoredRef.current = true;
    try {
      const raw = localStorage.getItem(STORAGE_KEY);
      if (!raw) return;
      const parsed = JSON.parse(raw);
      if (parsed?.form) {
        setForm({ ...DEFAULT_FORM, ...parsed.form });
        toast.message("Welcome back, we restored your progress.", {
          duration: 4000,
        });
      }
    } catch {
      /* ignore corrupted state */
    }
  }, []);

  // Persist form state on every change (debounced via React batching)
  useEffect(() => {
    if (!restoredRef.current) return;
    try {
      localStorage.setItem(STORAGE_KEY, JSON.stringify({ form }));
    } catch {
      /* quota or private mode , ignore */
    }
  }, [form]);

  // Fetch form options
  useEffect(() => {
    let cancelled = false;
    axios
      .get(`${API}/patients/form-options`)
      .then((r) => {
        if (!cancelled) setOptions(r.data);
      })
      .catch(() => toast.error("Could not load registration form options."))
      .finally(() => {
        if (!cancelled) setOptionsLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, []);

  const setField = (key, value) =>
    setForm((f) => ({ ...f, [key]: value }));

  const setCondition = (idx, key, value) =>
    setForm((f) => {
      const conditions = f.conditions.map((c, i) =>
        i === idx ? { ...c, [key]: value } : c,
      );
      return { ...f, conditions };
    });

  const addCondition = () =>
    setForm((f) => ({
      ...f,
      conditions: [...f.conditions, { ...DEFAULT_CONDITION }],
    }));

  const removeCondition = (idx) =>
    setForm((f) => ({
      ...f,
      conditions:
        f.conditions.length === 1
          ? f.conditions
          : f.conditions.filter((_, i) => i !== idx),
    }));

  const setConsent = (key, value) =>
    setForm((f) => ({ ...f, consents: { ...f.consents, [key]: value } }));

  const tickAllConsents = () =>
    setForm((f) => ({
      ...f,
      consents: Object.fromEntries(
        Object.keys(f.consents).map((k) => [k, true]),
      ),
    }));

  // When the country changes, auto-fill phone prefix
  const onCountryChange = (countryName) => {
    const c = options.countries.find((x) => x.name === countryName);
    setForm((f) => ({
      ...f,
      country: countryName,
      // Always sync prefix to the chosen country (more predictable than only-if-blank)
      phone_prefix: c ? c.prefix : f.phone_prefix,
    }));
  };

  // --- Per-section validators ------------------------------------------------
  // Each returns a partial errors object scoped to that section. Called for
  // single-page submission (all together) and for the `completedIds` derivation
  // which drives the section-nav check icons.
  const validateAbout = () => {
    const e = {};
    if (!form.first_name.trim()) e.first_name = "Required";
    if (!form.last_name.trim()) e.last_name = "Required";
    if (!form.email.trim() || !/.+@.+\..+/.test(form.email))
      e.email = "Enter a valid email";
    if (!form.date_of_birth || form.date_of_birth.length !== 10)
      e.date_of_birth = "Pick day, month and year";
    const age = calcAge(form.date_of_birth);
    if (age !== null && age < 16) e.date_of_birth = "Must be 16 or older";
    if (!form.gender) e.gender = "Required";
    if (!form.ethnicity) e.ethnicity = "Required";
    if (
      form.ethnicity === "Multiple ethnicity/ Other (please specify)" &&
      !form.ethnicity_specify.trim()
    )
      e.ethnicity_specify = "Please specify";
    return e;
  };
  const validateLocation = () => {
    const e = {};
    if (!form.country) e.country = "Required";
    if (!form.postal_code.trim()) e.postal_code = "Required";
    else {
      const r = validatePostalCode(form.country, form.postal_code);
      if (!r.valid) e.postal_code = r.hint;
    }
    if (!form.phone_prefix.trim()) e.phone_prefix = "Required";
    if (
      !form.phone_number.trim() ||
      form.phone_number.replace(/\D/g, "").length < 6
    )
      e.phone_number = "Enter a valid mobile number";
    return e;
  };
  const validateConditions = () => {
    const e = {};
    form.conditions.forEach((c, i) => {
      if (!c.condition_label) e[`condition_${i}_label`] = "Choose a condition";
      if (c.condition_label === "Other" && !c.specify?.trim())
        e[`condition_${i}_specify`] = "Please specify";
    });
    return e;
  };
  const validateConsents = () => {
    const e = {};
    CONSENT_ITEMS.forEach((c) => {
      if (!form.consents[c.key]) e[`consent_${c.key}`] = "Required";
    });
    return e;
  };

  // Section completion (for the SectionNav check icons).
  const completedIds = useMemo(() => {
    const set = new Set();
    if (Object.keys(validateAbout()).length === 0) set.add("about");
    if (Object.keys(validateLocation()).length === 0) set.add("location");
    if (Object.keys(validateConditions()).length === 0) set.add("conditions");
    if (Object.keys(validateConsents()).length === 0) set.add("consents");
    return set;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [form]);

  const validateAll = () => {
    const e = {
      ...validateAbout(),
      ...validateLocation(),
      ...validateConditions(),
      ...validateConsents(),
    };
    setErrors(e);
    return e;
  };

  const submit = async () => {
    setSubmitAttempted(true);
    const e = validateAll();
    if (Object.keys(e).length > 0) {
      // Scroll to the first invalid section so user can fix it.
      const order = [
        { id: "about", keys: ["first_name", "last_name", "email", "date_of_birth", "gender", "ethnicity", "ethnicity_specify"] },
        { id: "location", keys: ["country", "postal_code", "phone_prefix", "phone_number"] },
        { id: "conditions", keys: Object.keys(e).filter((k) => k.startsWith("condition_")) },
        { id: "consents", keys: Object.keys(e).filter((k) => k.startsWith("consent_")) },
      ];
      const firstSection = order.find((s) =>
        s.keys.some((k) => k in e)
      );
      if (firstSection) {
        const el = document.getElementById(firstSection.id);
        if (el) {
          const top = el.getBoundingClientRect().top + window.scrollY - 132;
          window.scrollTo({ top, behavior: "smooth" });
        }
      }
      toast.error("Please complete the highlighted fields.");
      return;
    }
    setSubmitting(true);
    setDuplicateEmail(null);
    try {
      const payload = {
        ...form,
        ethnicity_specify: form.ethnicity_specify || null,
      };
      await axios.post(`${API}/patients/register`, payload);
      // Clear saved progress on success
      try {
        localStorage.removeItem(STORAGE_KEY);
      } catch {
        /* ignore */
      }
      setSubmitted(true);
      window.scrollTo({ top: 0, behavior: "smooth" });
    } catch (err) {
      const status = err.response?.status;
      const detail = err.response?.data?.detail;
      if (status === 409) {
        setDuplicateEmail(form.email);
        toast.error("This email is already registered.");
        const el = document.getElementById("consents");
        if (el) {
          const top = el.getBoundingClientRect().top + window.scrollY - 132;
          window.scrollTo({ top, behavior: "smooth" });
        }
      } else if (status === 422) {
        toast.error(
          "Some fields look invalid. Please scroll up and check each section.",
        );
      } else {
        toast.error(
          typeof detail === "string"
            ? detail
            : "Registration failed. Please try again or contact us.",
        );
      }
    } finally {
      setSubmitting(false);
    }
  };

  if (submitted) {
    return <SuccessState />;
  }

  const consentsAllOk = Object.values(form.consents).every(Boolean);

  const SECTIONS = [
    { id: "about", label: "About you" },
    { id: "location", label: "Location & contact" },
    { id: "conditions", label: "Your conditions" },
    { id: "consents", label: "Your rights" },
  ];

  return (
    <SignUpShell
      audience="patient"
      mode="single-page"
      eyebrow="Patient sign-up"
      heroTitle="Join the MediTalk patient community"
      heroSubtitle="Tell us a little about you and any conditions you live with. We'll only contact you about studies that match. You can withdraw at any time."
      timeEstimate="Takes about 3 minutes"
      backTo="/patient"
      backLabel="Back to Patient Hub"
      sections={SECTIONS}
      completedIds={completedIds}
      mobileCta={
        <button
          type="button"
          onClick={submit}
          disabled={submitting}
          className="w-full btn-teal !py-2.5 disabled:opacity-60 disabled:cursor-not-allowed inline-flex items-center justify-center gap-2"
          data-testid="submit-register-btn-mobile"
        >
          {submitting ? (
            <>
              <Loader2 size={18} className="animate-spin" />
              Submitting…
            </>
          ) : (
            <>
              Submit registration
              <Check size={18} />
            </>
          )}
        </button>
      }
    >
      <div data-testid="patient-signup-form" className="space-y-14">
        <section id="about" className="scroll-mt-32">
          <Step1
            form={form}
            setField={setField}
            options={options}
            optionsLoading={optionsLoading}
            errors={errors}
          />
        </section>

        <section id="location" className="scroll-mt-32 pt-2 border-t border-[var(--light-border)]">
          <Step2
            form={form}
            setField={setField}
            onCountryChange={onCountryChange}
            options={options}
            optionsLoading={optionsLoading}
            errors={errors}
          />
        </section>

        <section id="conditions" className="scroll-mt-32 pt-2 border-t border-[var(--light-border)]">
          <Step3
            form={form}
            setCondition={setCondition}
            addCondition={addCondition}
            removeCondition={removeCondition}
            options={options}
            optionsLoading={optionsLoading}
            errors={errors}
          />
        </section>

        <section id="consents" className="scroll-mt-32 pt-2 border-t border-[var(--light-border)]">
          <Step4
            form={form}
            setConsent={setConsent}
            tickAll={tickAllConsents}
            errors={errors}
            duplicateEmail={duplicateEmail}
          />
        </section>

        {/* Desktop submit */}
        <div className="hidden md:block pt-6 border-t border-[var(--light-border)]">
          <ValidationSummary
            visible={submitAttempted}
            sections={SECTIONS.map((s) => ({ ...s, ok: completedIds.has(s.id) }))}
          />
          <button
            type="button"
            onClick={submit}
            disabled={submitting}
            className="w-full md:w-auto btn-teal inline-flex items-center justify-center gap-2 disabled:opacity-60 disabled:cursor-not-allowed"
            data-testid="submit-registration-btn"
          >
            {submitting ? (
              <>
                <Loader2 size={18} className="animate-spin" />
                Submitting…
              </>
            ) : (
              <>
                Submit registration
                <Check size={18} />
              </>
            )}
          </button>
          {!consentsAllOk && (
            <p className="mt-3 text-sm text-[var(--text-muted)]">
              You'll need to confirm all 3 statements above before submitting.
            </p>
          )}
        </div>
      </div>
    </SignUpShell>
  );
}

// ============================================================
// Sub-components
// ============================================================

function FieldLabel({ children, error, helper }) {
  return (
    <div className="mb-2">
      <label className="block text-[15px] font-semibold text-[var(--charcoal)]">
        {children}
        {error && (
          <span className="ml-2 text-xs font-medium text-red-600">{error}</span>
        )}
      </label>
      {helper && !error && (
        <span className="text-[12.5px] text-[#475569]">{helper}</span>
      )}
    </div>
  );
}

const inputCls =
  "w-full h-12 px-4 rounded-xl border border-[var(--light-border)] bg-white text-[15px] text-[var(--charcoal)] placeholder:text-[var(--text-muted)]/70 transition-all focus:outline-none focus:border-[var(--lime)] focus:ring-4 focus:ring-[var(--lime)]/20";

const inputClsErr =
  "w-full h-12 px-4 rounded-xl border border-red-300 bg-white text-[15px] text-[var(--charcoal)] transition-all focus:outline-none focus:border-red-400 focus:ring-4 focus:ring-red-200/40";

const selectCls = inputCls + " appearance-none cursor-pointer pr-10 bg-[url('data:image/svg+xml;utf8,<svg xmlns=%22http://www.w3.org/2000/svg%22 width=%2212%22 height=%2212%22 viewBox=%220 0 24 24%22 fill=%22none%22 stroke=%22%23475569%22 stroke-width=%222%22 stroke-linecap=%22round%22 stroke-linejoin=%22round%22><polyline points=%226 9 12 15 18 9%22/></svg>')] bg-no-repeat bg-[right_0.875rem_center]";

const textareaCls =
  "w-full px-4 py-3 rounded-xl border border-[var(--light-border)] bg-white text-[15px] text-[var(--charcoal)] placeholder:text-[var(--text-muted)]/70 transition-all focus:outline-none focus:border-[var(--lime)] focus:ring-4 focus:ring-[var(--lime)]/20";

function Skeleton({ className = "" }) {
  return (
    <div
      className={`animate-pulse bg-[var(--bone-soft)] rounded-lg ${className}`}
    />
  );
}

// ----------- Step 1 -----------
function Step1({ form, setField, options, optionsLoading, errors }) {
  const showSpecify =
    form.ethnicity === "Multiple ethnicity/ Other (please specify)";
  const age = calcAge(form.date_of_birth);

  return (
    <div className="space-y-5" data-testid="register-step-1">
      <div>
        <h2 className="text-2xl font-['Outfit'] font-bold text-[var(--charcoal)]">
          About you
        </h2>
        <p className="text-sm text-[var(--text-muted)] mt-1">
          Basic info so studies can confirm you match their criteria. Used for
          eligibility only.
        </p>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
        <div>
          <FieldLabel error={errors.first_name}>First name *</FieldLabel>
          <input
            data-testid="reg-first-name"
            className={errors.first_name ? inputClsErr : inputCls}
            value={form.first_name}
            onChange={(e) => setField("first_name", e.target.value)}
            autoComplete="given-name"
          />
        </div>
        <div>
          <FieldLabel error={errors.last_name}>Last name *</FieldLabel>
          <input
            data-testid="reg-last-name"
            className={errors.last_name ? inputClsErr : inputCls}
            value={form.last_name}
            onChange={(e) => setField("last_name", e.target.value)}
            autoComplete="family-name"
          />
        </div>
        <div className="md:col-span-2">
          <FieldLabel error={errors.email}>Email *</FieldLabel>
          <input
            data-testid="reg-email"
            type="email"
            inputMode="email"
            className={errors.email ? inputClsErr : inputCls}
            value={form.email}
            onChange={(e) => setField("email", e.target.value)}
            autoComplete="email"
          />
        </div>

        <div className="md:col-span-2">
          <FieldLabel error={errors.date_of_birth}>Date of birth *</FieldLabel>
          <DateOfBirthPicker
            data-testid="reg-dob"
            value={form.date_of_birth}
            onChange={(v) => setField("date_of_birth", v)}
          />
          {age !== null && (
            <span
              className="mt-2 inline-flex items-center gap-1.5 px-3 py-1 rounded-full bg-[var(--lime)]/15 text-[var(--charcoal)] text-xs font-semibold"
              data-testid="dob-age-preview"
            >
              <span className="h-1.5 w-1.5 rounded-full bg-[var(--lime)]" />
              {age} years old
            </span>
          )}
          <WhyWeAsk>
            We use this only to confirm age eligibility. Most studies have an age range.
          </WhyWeAsk>
        </div>

        <div>
          <FieldLabel error={errors.gender}>Gender *</FieldLabel>
          {optionsLoading ? (
            <Skeleton className="h-11" />
          ) : (
            <select
              data-testid="reg-gender"
              className={errors.gender ? inputClsErr : selectCls}
              value={form.gender}
              onChange={(e) => setField("gender", e.target.value)}
            >
              <option value="">Select…</option>
              {options.genders.map((g) => (
                <option key={g} value={g}>
                  {g}
                </option>
              ))}
            </select>
          )}
        </div>

        <div>
          <FieldLabel error={errors.ethnicity}>Ethnicity *</FieldLabel>
          {optionsLoading ? (
            <Skeleton className="h-11" />
          ) : (
            <select
              data-testid="reg-ethnicity"
              className={errors.ethnicity ? inputClsErr : selectCls}
              value={form.ethnicity}
              onChange={(e) => setField("ethnicity", e.target.value)}
            >
              <option value="">Select…</option>
              {options.ethnicities.map((e) => (
                <option key={e} value={e}>
                  {e}
                </option>
              ))}
            </select>
          )}
          <WhyWeAsk>
            Diverse representation in research is critical for health equity.
          </WhyWeAsk>
        </div>

        {showSpecify && (
          <div className="md:col-span-2">
            <FieldLabel error={errors.ethnicity_specify}>
              Please specify *
            </FieldLabel>
            <input
              data-testid="reg-ethnicity-specify"
              className={errors.ethnicity_specify ? inputClsErr : inputCls}
              value={form.ethnicity_specify}
              onChange={(e) => setField("ethnicity_specify", e.target.value)}
            />
          </div>
        )}
      </div>
    </div>
  );
}

// ----------- Step 2 -----------
function Step2({ form, setField, onCountryChange, options, optionsLoading, errors }) {
  const countryOptions = useMemo(
    () =>
      options.countries.map((c) => ({
        value: c.name,
        label: c.name,
      })),
    [options.countries],
  );

  return (
    <div className="space-y-5" data-testid="register-step-2">
      <div>
        <h2 className="text-2xl font-['Outfit'] font-bold text-[var(--charcoal)]">
          Location & contact
        </h2>
        <p className="text-sm text-[var(--text-muted)] mt-1">
          Many studies are country-specific. We'll use this to filter the
          opportunities we send you.
        </p>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
        <div className="md:col-span-2">
          <FieldLabel error={errors.country}>
            Country *
          </FieldLabel>
          {optionsLoading ? (
            <Skeleton className="h-11" />
          ) : (
            <Combobox
              data-testid="reg-country"
              options={countryOptions}
              value={form.country}
              onChange={onCountryChange}
              placeholder="Type to search…"
              error={!!errors.country}
            />
          )}
        </div>

        <div className="md:col-span-2">
          <FieldLabel error={errors.postal_code}>
            Postal / ZIP code *
          </FieldLabel>
          <input
            data-testid="reg-postal"
            className={errors.postal_code ? inputClsErr : inputCls}
            value={form.postal_code}
            onChange={(e) => setField("postal_code", e.target.value)}
            autoComplete="postal-code"
            placeholder={form.country === "United States" ? "12345" : ""}
          />
        </div>

        <div>
          <FieldLabel error={errors.phone_prefix}>Country code *</FieldLabel>
          <input
            data-testid="reg-phone-prefix"
            className={errors.phone_prefix ? inputClsErr : inputCls}
            value={form.phone_prefix}
            onChange={(e) => setField("phone_prefix", e.target.value)}
            placeholder="+1"
            inputMode="tel"
          />
        </div>
        <div>
          <FieldLabel error={errors.phone_number}>Mobile number *</FieldLabel>
          <input
            data-testid="reg-phone-number"
            className={errors.phone_number ? inputClsErr : inputCls}
            value={form.phone_number}
            onChange={(e) => setField("phone_number", e.target.value)}
            placeholder="6187805565"
            autoComplete="tel-national"
            inputMode="tel"
          />
        </div>
      </div>
      <WhyWeAsk>
        We only call if a study is a strong match. No marketing calls, ever.
      </WhyWeAsk>
    </div>
  );
}

// ----------- Step 3 -----------
function Step3({
  form,
  setCondition,
  addCondition,
  removeCondition,
  options,
  optionsLoading,
  errors,
}) {
  const conditionChoices = useMemo(() => {
    const list = options.conditions || [];
    return list.includes("Other") ? list : [...list, "Other"];
  }, [options.conditions]);

  return (
    <div className="space-y-5" data-testid="register-step-3">
      <div>
        <h2 className="text-2xl font-['Outfit'] font-bold text-[var(--charcoal)]">
          Your conditions
        </h2>
        <p className="text-sm text-[var(--text-muted)] mt-1">
          Add every condition you'd like to be considered for. The more we know,
          the better we can match you. Choose <strong>Other</strong> if your
          condition isn't listed.
        </p>
      </div>

      {form.conditions.map((cond, idx) => (
        <ConditionCard
          key={idx}
          idx={idx}
          cond={cond}
          setCondition={setCondition}
          removeCondition={removeCondition}
          conditionChoices={conditionChoices}
          options={options}
          optionsLoading={optionsLoading}
          errors={errors}
          canRemove={form.conditions.length > 1}
        />
      ))}

      <button
        type="button"
        onClick={addCondition}
        className="w-full inline-flex items-center justify-center gap-2 px-4 py-4 rounded-2xl bg-[var(--lime)] text-[var(--dark-bg)] font-semibold text-[15px] hover:bg-[var(--lime-dark)] transition-all shadow-[0_8px_24px_-12px_rgba(196,247,75,0.7)] hover:shadow-[0_12px_30px_-10px_rgba(196,247,75,0.9)] hover:-translate-y-0.5"
        data-testid="add-condition-btn"
      >
        <Plus size={18} strokeWidth={2.5} />
        Add another condition you live with
      </button>
    </div>
  );
}

function ConditionCard({
  idx,
  cond,
  setCondition,
  removeCondition,
  conditionChoices,
  options,
  optionsLoading,
  errors,
  canRemove,
}) {
  return (
    <div
      className="rounded-2xl border border-[var(--light-border)] bg-[var(--bone-soft)] p-5 md:p-6 space-y-4 transition-all hover:border-[var(--lime)] hover:shadow-[0_10px_30px_-12px_rgba(196,247,75,0.35)] animate-[stepIn_300ms_ease-out_forwards]"
      data-testid={`condition-${idx}`}
    >
      <div className="flex items-center justify-between">
        <span className="inline-flex items-center gap-2 text-xs uppercase tracking-[0.15em] font-semibold text-[var(--charcoal)]">
          <span className="h-1.5 w-1.5 rounded-full bg-[var(--lime)]" />
          Condition #{idx + 1}
        </span>
        {canRemove && (
          <button
            type="button"
            onClick={() => removeCondition(idx)}
            className="inline-flex items-center gap-1 text-xs text-[var(--text-muted)] hover:text-red-600 transition-colors"
            data-testid={`remove-condition-btn-${idx}`}
          >
            <Trash2 size={14} /> Remove
          </button>
        )}
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <div className="md:col-span-2">
          <FieldLabel error={errors[`condition_${idx}_label`]}>
            Condition *
          </FieldLabel>
          {optionsLoading ? (
            <Skeleton className="h-11" />
          ) : (
            <Combobox
              data-testid={`reg-condition-${idx}-label`}
              options={conditionChoices}
              value={cond.condition_label}
              onChange={(v) => setCondition(idx, "condition_label", v)}
              placeholder="Type to search 200+ conditions…"
              pinnedValues={options.popular_conditions || []}
              pinnedLabel="Most common"
              error={!!errors[`condition_${idx}_label`]}
            />
          )}
        </div>

        {cond.condition_label === "Other" && (
          <div className="md:col-span-2">
            <FieldLabel error={errors[`condition_${idx}_specify`]}>
              Please specify *
            </FieldLabel>
            <input
              data-testid={`reg-condition-${idx}-specify`}
              className={
                errors[`condition_${idx}_specify`] ? inputClsErr : inputCls
              }
              value={cond.specify || ""}
              onChange={(e) => setCondition(idx, "specify", e.target.value)}
              placeholder="e.g. Immune Thrombocytopenia Purpura"
            />
          </div>
        )}

        <div>
          <FieldLabel>
            Approx. Diagnosis Date
          </FieldLabel>
          <MonthYearPicker
            value={cond.diagnosis_date || ""}
            onChange={(v) => setCondition(idx, "diagnosis_date", v)}
          />
        </div>

        <div>
          <FieldLabel>Severity</FieldLabel>
          <SegmentedControl
            data-testid={`reg-condition-${idx}-severity`}
            options={["Mild", "Moderate", "Severe"]}
            value={cond.severity || ""}
            onChange={(v) => setCondition(idx, "severity", v)}
            ariaLabel="Severity"
          />
        </div>

        <div className="md:col-span-2">
          <FieldLabel>Treatment</FieldLabel>
          <input
            className={inputCls}
            value={cond.treatment || ""}
            onChange={(e) => setCondition(idx, "treatment", e.target.value)}
            placeholder="e.g. Antihistamines, biologics, surgery, none"
          />
        </div>
        <div className="md:col-span-2">
          <FieldLabel>Anything else we should know? (optional)</FieldLabel>
          <textarea
            rows={2}
            className={textareaCls}
            value={cond.other_info || ""}
            onChange={(e) => setCondition(idx, "other_info", e.target.value)}
            placeholder="Anything that might affect study eligibility"
          />
        </div>
      </div>
    </div>
  );
}

// ----------- Step 4 -----------
function Step4({ form, setConsent, tickAll, errors, duplicateEmail }) {
  const allTicked = Object.values(form.consents).every(Boolean);
  const tickedCount = Object.values(form.consents).filter(Boolean).length;
  return (
    <div className="space-y-5" data-testid="register-step-4">
      <div>
        <h2 className="text-2xl md:text-3xl font-['Outfit'] font-bold text-[var(--charcoal)]">
          Your rights, in plain English
        </h2>
        <p className="text-sm text-[var(--text-muted)] mt-2 leading-relaxed max-w-xl">
          Three short statements that protect you. Each has a plain summary
          and the full legal wording one tap away. All three are required.
        </p>
      </div>

      {duplicateEmail && (
        <div
          className="rounded-2xl border border-amber-300 bg-amber-50 p-4 text-sm text-[var(--charcoal)] flex items-start gap-3"
          data-testid="duplicate-email-banner"
        >
          <span className="mt-0.5 text-amber-600">●</span>
          <div>
            <p className="font-semibold mb-1">You're already registered</p>
            <p className="text-[var(--text-muted)] leading-relaxed">
              with <span className="font-mono">{maskEmail(duplicateEmail)}</span>.{" "}
              <Link
                to="/patient/update"
                className="text-[var(--teal)] underline font-medium"
              >
                Update your details →
              </Link>
            </p>
          </div>
        </div>
      )}

      <div className="flex items-center justify-between gap-3 flex-wrap p-4 rounded-2xl bg-[var(--bone-soft)] border border-[var(--light-border)]">
        <p className="text-sm text-[var(--charcoal)] font-medium">
          <span className="text-[var(--teal)] font-bold">{tickedCount}</span>
          <span className="text-[var(--text-muted)]"> of 3 confirmed</span>
        </p>
        <button
          type="button"
          onClick={tickAll}
          disabled={allTicked}
          className="inline-flex items-center gap-2 px-4 py-2 rounded-full text-sm font-semibold bg-[var(--lime)] text-[var(--dark-bg)] hover:bg-[var(--lime-dark)] transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
          data-testid="tick-all-consents-btn"
        >
          <Check size={16} strokeWidth={3} />
          {allTicked ? "All confirmed" : "Confirm all (recommended)"}
        </button>
      </div>

      <div className="space-y-3">
        {CONSENT_ITEMS.map((c) => (
          <ConsentItem
            key={c.key}
            item={c}
            checked={form.consents[c.key]}
            error={errors[`consent_${c.key}`]}
            onChange={(v) => setConsent(c.key, v)}
          />
        ))}
      </div>
    </div>
  );
}

function ConsentItem({ item, checked, error, onChange }) {
  const [expanded, setExpanded] = useState(false);
  return (
    <div
      className={`rounded-2xl border transition-all overflow-hidden ${
        checked
          ? "border-[var(--lime)] bg-[var(--lime)]/[0.06]"
          : error
          ? "border-red-300 bg-red-50/40"
          : "border-[var(--light-border)] bg-white hover:border-[var(--charcoal)]/20"
      }`}
    >
      <label
        htmlFor={`consent-${item.key}`}
        className="flex gap-3 items-start p-4 cursor-pointer"
      >
        {/* Custom checkbox */}
        <span
          className={`mt-0.5 flex h-6 w-6 items-center justify-center rounded-md border-2 flex-shrink-0 transition-all ${
            checked
              ? "bg-[var(--lime)] border-[var(--lime)]"
              : "bg-white border-[var(--light-border)]"
          }`}
          aria-hidden="true"
        >
          {checked && (
            <Check
              size={14}
              strokeWidth={3.5}
              className="text-[var(--dark-bg)]"
            />
          )}
        </span>
        <input
          id={`consent-${item.key}`}
          data-testid={`consent-${item.key}`}
          type="checkbox"
          className="sr-only"
          checked={checked}
          onChange={(e) => onChange(e.target.checked)}
        />
        <div className="min-w-0 flex-1">
          <span className="block font-semibold text-[var(--charcoal)] text-sm md:text-base mb-1">
            {item.title}
          </span>
          <p className="text-sm text-[var(--text-muted)] leading-relaxed">
            {item.summary}
          </p>
        </div>
      </label>

      <button
        type="button"
        onClick={() => setExpanded((x) => !x)}
        className="px-4 pb-3 -mt-1 inline-flex items-center gap-1 text-xs text-[var(--text-muted)] hover:text-[var(--charcoal)] transition-colors"
        data-testid={`consent-${item.key}-toggle`}
      >
        <ChevronDown
          size={12}
          className={`transition-transform ${expanded ? "rotate-180" : ""}`}
        />
        {expanded ? "Hide full text" : "Read full text"}
      </button>
      {expanded && (
        <p
          className="px-4 pb-4 text-xs text-[var(--text-muted)] leading-relaxed border-t border-[var(--light-border)] pt-3"
          data-testid={`consent-${item.key}-full`}
        >
          {item.full}
        </p>
      )}
    </div>
  );
}

// ============================================================
// Helpers / Success
// ============================================================
function maskEmail(email) {
  if (!email) return "";
  const [local, domain] = email.split("@");
  if (!domain) return email;
  const first = local.slice(0, 1);
  const rest = "*".repeat(Math.max(local.length - 1, 1));
  return `${first}${rest}@${domain}`;
}

function SuccessState() {
  const [copied, setCopied] = useState(false);
  const url =
    typeof window !== "undefined"
      ? `${window.location.origin}/patient`
      : "https://meditalk.world/patient";

  const onShare = async () => {
    try {
      if (navigator.share) {
        await navigator.share({
          title: "MediTalk",
          text:
            "Get paid to share your healthcare experience with MediTalk. Fully remote interviews, surveys and bulletin boards.",
          url,
        });
      } else {
        await navigator.clipboard.writeText(url);
        setCopied(true);
        toast.success("Link copied to clipboard");
        setTimeout(() => setCopied(false), 2200);
      }
    } catch {
      /* user dismissed */
    }
  };

  return (
    <div className="min-h-screen">
      <section className="bg-[var(--dark-bg)] grain-dark relative overflow-hidden">
        {/* Lime burst orbs for celebration */}
        <div
          aria-hidden="true"
          className="pointer-events-none absolute top-[-160px] left-1/2 -translate-x-1/2 h-[480px] w-[480px] rounded-full bg-[var(--lime)]/15 blur-3xl"
        />
        <div
          aria-hidden="true"
          className="pointer-events-none absolute top-[100px] -right-32 h-72 w-72 rounded-full bg-[var(--lime)]/10 blur-3xl"
        />
        <div className="container-main py-20 md:py-24 max-w-3xl text-center relative z-10">
          {/* Animated lime tick */}
          <div className="inline-flex items-center justify-center w-20 h-20 rounded-full bg-[var(--lime)] mb-7 animate-[bounceIn_500ms_ease-out]">
            <Check
              size={40}
              strokeWidth={3}
              className="text-[var(--dark-bg)]"
            />
          </div>
          <span className="inline-flex items-center gap-2 text-xs uppercase tracking-[0.18em] font-semibold text-[var(--lime)] mb-4">
            <span className="w-6 h-px bg-[var(--lime)]" />
            Welcome to the community
          </span>
          <h1 className="text-4xl md:text-5xl lg:text-6xl font-['Outfit'] font-bold text-white mb-5 leading-[1.05]">
            Your voice
            <span className="text-[var(--lime)]"> matters.</span>
          </h1>
          <p className="text-lg text-white/70 mb-12 max-w-xl mx-auto leading-relaxed">
            Thanks for registering. We'll only get in touch when there's a
            study that matches your profile, never before.
          </p>

          {/* Next-steps cards */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-12">
            <Link
              to="/patient/studies"
              className="group rounded-2xl bg-white/[0.04] border border-white/10 p-6 text-left hover:bg-white/[0.08] hover:border-[var(--lime)]/40 transition-all"
              data-testid="success-view-studies"
            >
              <p className="text-[10px] uppercase tracking-[0.18em] font-semibold text-[var(--lime)] mb-2">
                Step 1
              </p>
              <h3 className="font-['Outfit'] font-semibold text-white mb-2">
                Browse open studies
              </h3>
              <p className="text-xs text-white/60 leading-relaxed mb-4">
                See what's open right now and express interest in anything that catches your eye.
              </p>
              <span className="inline-flex items-center gap-1 text-sm font-semibold text-[var(--lime)]">
                Browse <ArrowRight size={14} className="transition-transform group-hover:translate-x-0.5" />
              </span>
            </Link>
            <div className="rounded-2xl bg-white/[0.04] border border-white/10 p-6 text-left">
              <p className="text-[10px] uppercase tracking-[0.18em] font-semibold text-[var(--lime)] mb-2">
                Step 2
              </p>
              <h3 className="font-['Outfit'] font-semibold text-white mb-2">
                Check your inbox
              </h3>
              <p className="text-xs text-white/60 leading-relaxed">
                When a study matches your profile, we'll email you with the details. Reply to opt in.
              </p>
            </div>
            <button
              type="button"
              onClick={onShare}
              className="group rounded-2xl bg-[var(--lime)]/10 border border-[var(--lime)]/30 p-6 text-left hover:bg-[var(--lime)]/15 transition-all"
              data-testid="success-share"
            >
              <p className="text-[10px] uppercase tracking-[0.18em] font-semibold text-[var(--lime)] mb-2">
                Step 3
              </p>
              <h3 className="font-['Outfit'] font-semibold text-white mb-2">
                {copied ? "Link copied" : "Share MediTalk"}
              </h3>
              <p className="text-xs text-white/60 leading-relaxed mb-4">
                Know someone whose voice should be heard? Send them in.
              </p>
              <span className="inline-flex items-center gap-1 text-sm font-semibold text-[var(--lime)]">
                {copied ? <Copy size={14} /> : <Share2 size={14} />}
                {copied ? "Copied" : "Share"}
              </span>
            </button>
          </div>

          {/* Soft secondary touchpoint , a follow strip for visitors
              who'd rather hear from us on social than via email. */}
          <div className="mb-10 text-left">
            <FollowStrip
              variant="dark"
              source="patient-success"
              campaign="post-signup"
              medium="success-page"
              title="Follow us for new study announcements"
              subtitle="We post new studies on Facebook, Instagram, TikTok and X , often before they go live on the site."
              testId="patient-success-follow-strip"
            />
          </div>

          <Link
            to="/"
            className="inline-flex items-center justify-center gap-2 text-sm text-white/65 hover:text-white transition-colors"
            data-testid="success-home"
          >
            <ArrowLeft size={14} />
            Back to home
          </Link>
        </div>
      </section>
      <style>{`
        @keyframes bounceIn {
          0%   { transform: scale(0); opacity: 0; }
          60%  { transform: scale(1.15); opacity: 1; }
          100% { transform: scale(1); }
        }
      `}</style>
    </div>
  );
}
